import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "../client";
import type {
  BatchRenewalResponse,
  CheckinResult,
  CheckoutResult,
  CirculationError,
  Loan,
  PatronRenewalsResponse,
  PatronSummaryResponse,
  RenewalEligibility,
  RenewalResult,
  Reservation,
} from "../types";

const LOANS_KEY = "loans";
const RESERVATIONS_KEY = "reservations";
const PATRON_SUMMARY_KEY = "patron-summary";
const PATRON_RENEWALS_KEY = "patron-renewals";

/**
 * Get loans list.
 */
export function useLoans(status?: string, overdue?: boolean) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (overdue) params.set("overdue", "true");
  const queryString = params.toString();

  return useQuery({
    queryKey: [LOANS_KEY, status, overdue],
    queryFn: async () => {
      const url = queryString ? `/circulation/loans?${queryString}` : "/circulation/loans";
      return get<Loan[]>(url);
    },
  });
}

/**
 * Get reservations list.
 */
export function useReservations(status?: string) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const queryString = params.toString();

  return useQuery({
    queryKey: [RESERVATIONS_KEY, status],
    queryFn: async () => {
      const url = queryString
        ? `/circulation/reservations?${queryString}`
        : "/circulation/reservations";
      return get<Reservation[]>(url);
    },
  });
}

/**
 * Get patron summary for circulation desk.
 */
export function usePatronSummary(barcode: string | undefined) {
  return useQuery({
    queryKey: [PATRON_SUMMARY_KEY, barcode],
    queryFn: async () => {
      if (!barcode) throw new Error("Barcode is required");
      return get<PatronSummaryResponse>(`/circulation/patron/${barcode}/summary`);
    },
    enabled: !!barcode && barcode.length >= 4,
  });
}

/**
 * Checkout an item.
 */
export function useCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      patronBarcode?: string;
      patronId?: string;
      itemBarcode?: string;
      itemId?: string;
      locationId?: string;
      dueDate?: string;
    }) => {
      const result = await post<CheckoutResult | CirculationError>("/circulation/checkout", input);
      if ("error" in result) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LOANS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_SUMMARY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [RESERVATIONS_KEY] });
    },
  });
}

/**
 * Checkin an item.
 */
export function useCheckin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { itemBarcode?: string; itemId?: string; locationId?: string }) => {
      const result = await post<CheckinResult | CirculationError>("/circulation/checkin", input);
      if ("error" in result) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LOANS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_SUMMARY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [RESERVATIONS_KEY] });
    },
  });
}

/**
 * Renew a loan.
 */
export function useRenew() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { loanId?: string; itemBarcode?: string; itemId?: string }) => {
      const result = await post<RenewalResult | CirculationError>("/circulation/renew", input);
      if ("error" in result) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LOANS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_SUMMARY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_RENEWALS_KEY] });
    },
  });
}

/**
 * Get a patron's active loans with their renewal eligibility.
 */
export function usePatronRenewals(barcode: string | undefined) {
  return useQuery({
    queryKey: [PATRON_RENEWALS_KEY, barcode],
    queryFn: async () => {
      if (!barcode) throw new Error("Barcode is required");
      return get<PatronRenewalsResponse>(
        `/circulation/patron/${encodeURIComponent(barcode)}/renewals`
      );
    },
    enabled: !!barcode,
    // A missing patron is an answer, not a transient failure
    retry: false,
  });
}

/**
 * Renew one, several or all loans of a patron. Each loan succeeds or fails on its own.
 */
export function useRenewPatronLoans() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patronBarcode,
      loanIds,
      overridePatronStatus = false,
    }: {
      patronBarcode: string;
      loanIds: string[];
      overridePatronStatus?: boolean;
    }) =>
      post<BatchRenewalResponse>(`/circulation/patron/${encodeURIComponent(patronBarcode)}/renew`, {
        loanIds,
        overridePatronStatus,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LOANS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_SUMMARY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_RENEWALS_KEY] });
    },
  });
}

/**
 * Check renewal eligibility.
 */
export function useRenewalEligibility(loanId: string | undefined) {
  return useQuery({
    queryKey: [LOANS_KEY, loanId, "eligibility"],
    queryFn: async () => {
      if (!loanId) throw new Error("Loan ID is required");
      return post<RenewalEligibility>("/circulation/renew/check", { loanId });
    },
    enabled: !!loanId,
  });
}

/**
 * Place a reservation.
 */
export function usePlaceReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      patronBarcode?: string;
      patronId?: string;
      biblioRecordId?: string;
      itemBarcode?: string;
      itemId?: string;
      pickupLocationId?: string;
      activationDate?: string;
    }) => {
      const result = await post<
        { success: true; reservation: Reservation; queuePosition: number } | CirculationError
      >("/circulation/reserve", input);
      if ("error" in result) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RESERVATIONS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_SUMMARY_KEY] });
    },
  });
}

/**
 * Cancel a reservation.
 */
export function useCancelReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const result = await post<{ success: true; reservation: Reservation } | CirculationError>(
        `/circulation/reservations/${id}/cancel`,
        { reason }
      );
      if ("error" in result) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RESERVATIONS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_SUMMARY_KEY] });
    },
  });
}

/**
 * Mark loan as lost.
 */
export function useMarkLost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      loanId,
      replacementCostInCents,
    }: {
      loanId: string;
      replacementCostInCents?: number;
    }) => {
      const result = await post<{ success: true; loan: Loan } | CirculationError>(
        `/circulation/loans/${loanId}/lost`,
        { replacementCostInCents }
      );
      if ("error" in result) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LOANS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PATRON_SUMMARY_KEY] });
    },
  });
}
